/**
 * Utility functions for managing localStorage with quota handling
 */
import { logger } from "../../utils/logger";

interface StorageOptions {
  maxRetries?: number;
  clearOnQuotaExceeded?: boolean;
  compress?: boolean;
}

/**
 * Simple compression function to reduce storage size
 */
const compressData = (data: string): string => {
  try {
    // Simple compression: remove unnecessary whitespace and use shorter property names
    const parsed = JSON.parse(data);
    return JSON.stringify(parsed);
  } catch {
    return data;
  }
};

/**
 * Safely store data in localStorage with quota handling
 */
export const safeSetItem = (
  key: string,
  value: any,
  options: StorageOptions = {}
): boolean => {
  const {
    maxRetries = 1,
    clearOnQuotaExceeded = true,
    compress = false,
  } = options;

  let dataToStore = typeof value === "string" ? value : JSON.stringify(value);

  // Apply compression if requested
  if (compress) {
    dataToStore = compressData(dataToStore);
  }

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      localStorage.setItem(key, dataToStore);
      return true;
    } catch (error) {
      if (error instanceof Error && error.name === "QuotaExceededError") {
        logger.warn(
          `localStorage quota exceeded for key: ${key}, attempt: ${attempt + 1}`
        );

        if (attempt < maxRetries && clearOnQuotaExceeded) {
          // Clear some non-essential data and retry
          clearNonEssentialData();
          continue;
        }
      }

      logger.error(`Failed to store ${key} in localStorage:`, error);
      return false;
    }
  }

  return false;
};

/**
 * Clear non-essential localStorage data to free up space
 */
export const clearNonEssentialData = (): void => {
  const nonEssentialKeys = [
    "alertsAllData",
    "alertsErrorsData",
    "alertsWarningsData",
    "alertsResolvedData",
    "alertsPendingData",
    "sortedHMAlertsRows",
    "backupHealthCheckMetricsData",
    "percetageOfMotionData",
    "top5DataUsage",
    "storedExpandedObject",
    "shiftedObj",
    "shapesObject",
    "backupProductsData",
    "backUpSiteSchedule",
    "backUpFlexSchedule",
  ];

  nonEssentialKeys.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      logger.warn(`Failed to remove ${key} from localStorage:`, error);
    }
  });
};

/**
 * Get the current localStorage usage
 */
export const getLocalStorageUsage = (): {
  used: number;
  available: number;
  percentage: number;
} => {
  let used = 0;
  let available = 0;

  try {
    // Calculate used space
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        used += localStorage[key].length + key.length;
      }
    }

    // Estimate available space (most browsers have ~5-10MB limit)
    // This is a rough estimate
    available = 5 * 1024 * 1024; // 5MB estimate

    // Try to get actual available space by testing storage
    const testKey = "__storage_test__";
    const testValue = "x".repeat(1024); // 1KB test

    for (let i = 0; i < 1000; i++) {
      // Try to store up to 1MB
      try {
        localStorage.setItem(testKey + i, testValue);
      } catch (e) {
        available = (i + 1) * 1024;
        break;
      }
    }

    // Clean up test data
    for (let i = 0; i < 1000; i++) {
      try {
        localStorage.removeItem(testKey + i);
      } catch (e) {
        break;
      }
    }
  } catch (error) {
    logger.warn("Could not calculate localStorage usage:", error);
  }

  const percentage = available > 0 ? (used / available) * 100 : 0;

  return { used, available, percentage };
};

/**
 * Check if localStorage is getting full
 */
export const isLocalStorageGettingFull = (threshold: number = 80): boolean => {
  const { percentage } = getLocalStorageUsage();
  return percentage > threshold;
};
